/** Human-readable descriptions of ledger entries (wallet transaction history). */
import { pairLabel } from './format.js';
const SIDE = { long: 'Buy', short: 'Sell' };
const CLOSE_DETAIL = {
    close: 'Realized P&L',
    manual: 'Realized P&L',
    tp: 'Realized P&L · take profit hit',
    sl: 'Realized P&L · stop loss hit',
    liquidation: 'Realized P&L · liquidated',
    admin: 'Realized P&L · closed by support',
};
const short = (s) => (s.length > 14 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s);
/**
 * Turns a ledger entry into a title and a detail line. Trading entries carry their position in `reason`
 * ("Close long BTC", "TP short ETH", "Open long GOLD", "Swap BTC"); funding entries carry network and tx/address.
 * Anything unrecognised falls back to the raw reason, so old or admin-written rows still read sensibly.
 */
export function describeLedger(e) {
    const reason = (e.reason ?? '').trim();
    let m;
    // A ticket our team opened in the CRM ("DEP-000001: <staff note>"): named by its number, and the
    // staff note stays internal. A CRM withdrawal is booked as a withdrawal (a debit adjustment before
    // 2026-10-12); either way it is the client's withdrawal.
    if (e.refType !== 'admin' && (e.type === 'deposit' || e.type === 'adjustment' || e.type === 'withdrawal') && (m = reason.match(/^(DEP|WD)-(\d{6,}):/))) {
        const out = (e.amount ?? '').startsWith('-');
        if (m[1] === 'DEP' && !out)
            return { kind: 'in', title: `Deposit DEP-${m[2]}`, detail: 'Booked by our team' };
        if (m[1] === 'WD' && out)
            return { kind: 'out', title: `Withdrawal WD-${m[2]}`, detail: 'Paid out by our team' };
    }
    switch (e.type) {
        case 'realized_pnl':
            if ((m = reason.match(/^(\w+)\s+(long|short)\s+(\S+)$/i))) {
                return { kind: 'trade', title: `Closed ${SIDE[m[2].toLowerCase()]} ${pairLabel(m[3])}`, detail: CLOSE_DETAIL[m[1].toLowerCase()] ?? 'Realized P&L' };
            }
            return { kind: 'trade', title: 'Realized P&L', detail: reason };
        case 'fee':
            if ((m = reason.match(/^(Open|Close)\s+(long|short)\s+(\S+)$/i))) {
                return { kind: 'fee', title: 'Trading fee', detail: `${m[1]} ${SIDE[m[2].toLowerCase()]} ${pairLabel(m[3])}` };
            }
            return { kind: 'fee', title: 'Trading fee', detail: reason };
        case 'swap':
            if ((m = reason.match(/^Swap\s+(\S+)$/i)))
                return { kind: 'fee', title: 'Swap', detail: `Overnight financing · ${pairLabel(m[1])}` };
            return { kind: 'fee', title: 'Swap', detail: reason };
        case 'deposit':
            if ((m = reason.match(/^(TRC20|ERC20|BEP20)\s+(\S+)$/)))
                return { kind: 'in', title: 'Deposit', detail: `${m[1]} · tx ${short(m[2])}` };
            return { kind: 'in', title: 'Deposit', detail: reason || 'Booked by support' };
        case 'withdrawal':
            if ((m = reason.match(/^WD-(\d+)\s+to\s+(\S+)\s+(\S+)$/)))
                return { kind: 'out', title: `Withdrawal WD-${m[1]}`, detail: `${m[2]} · ${short(m[3])}` };
            return { kind: 'out', title: 'Withdrawal', detail: reason };
        case 'withdrawal_refund':
            return { kind: 'in', title: 'Withdrawal returned', detail: reason || 'Cancelled or rejected' };
        case 'demo_reset':
            return { kind: 'other', title: 'Demo balance reset', detail: reason };
        // Our team's money actions (CRM Money menu, phase 2): the staff note stays internal.
        case 'dividend':
            return { kind: 'in', title: 'Dividend', detail: 'Paid by our team' };
        case 'capital_return':
            return { kind: 'out', title: 'Capital return', detail: 'Paid back to you by our team' };
        case 'chargeback':
            return { kind: 'out', title: 'Chargeback', detail: 'A deposit was reversed' };
        // Reverse and delete of an approved CRM ticket (2026-10-14). Staff views only: the client never gets
        // either entry of the pair (see REVERSAL_TYPES).
        case 'deposit_reversal':
            return { kind: 'out', title: 'Deposit reversed', detail: reason };
        case 'withdrawal_reversal':
            return { kind: 'in', title: 'Withdrawal reversed', detail: reason };
        // A nominal deposit / withdrawal from the CRM (2026-10-13): the client sees the money, never who booked it or why.
        case 'nominal':
            return (e.amount ?? '').startsWith('-') ? { kind: 'out', title: 'Funds removed', detail: '' } : { kind: 'in', title: 'Funds added', detail: '' };
        // Master Accounts fees, taken from the managed account.
        case 'management_fee':
            return { kind: 'out', title: 'Management fee', detail: reason || 'Managed account' };
        case 'performance_fee':
            return { kind: 'out', title: 'Performance fee', detail: reason || 'Managed account' };
        case 'credit':
            return (e.amount ?? '').startsWith('-')
                ? { kind: 'out', title: 'Credit removed', detail: reason || 'Trading credit' }
                : { kind: 'in', title: 'Credit added', detail: reason || 'Trading credit' };
        case 'adjustment':
            return { kind: 'other', title: 'Adjustment', detail: reason };
        case 'cashback':
            return { kind: 'in', title: 'Cashback claimed', detail: reason || 'Trading fee cashback' };
        case 'referral':
            return { kind: 'in', title: 'Referral rewards claimed', detail: reason || 'Referral commission' };
        case 'volume_reward':
            return (e.amount ?? '').startsWith('-')
                ? { kind: 'out', title: 'Volume reward reversed', detail: 'Taken back by our team' }
                : { kind: 'in', title: 'Volume reward claimed', detail: reason || 'Trading volume reward' };
        case 'yield_stake':
            return { kind: 'out', title: 'Moved to Yield', detail: reason || 'Yield plan' };
        case 'yield_redeem':
            return { kind: 'in', title: 'Returned from Yield', detail: reason || 'Yield plan' };
        case 'yield_interest':
            return { kind: 'in', title: 'Yield interest', detail: reason || 'Yield plan' };
        case 'pro_transfer_out':
            return { kind: 'out', title: 'Moved to Crypto Pro', detail: reason || 'Crypto Pro wallet' };
        case 'pro_transfer_in':
            return { kind: 'in', title: 'Moved from Crypto Pro', detail: reason || 'Crypto Pro wallet' };
        case 'pro_transfer_refund':
            return { kind: 'in', title: 'Crypto Pro move returned', detail: reason || 'The transfer did not go through' };
        default:
            return { kind: 'other', title: e.type, detail: reason };
    }
}
/**
 * Reverse and delete of an approved CRM funding ticket (2026-10-14). Each reverses one entry (its `reversesEntry`):
 * a `deposit` (taken back, a debit) or a `withdrawal` (given back, a credit). The client's History leaves out the
 * reversal and the entry it reverses; a statement does too when both fall in the same month, and otherwise shows
 * the reversal as one neutral BALANCE_CORRECTION line (the earlier month's statement was already issued).
 */
export const REVERSAL_TYPES = ['deposit_reversal', 'withdrawal_reversal'];
export const BALANCE_CORRECTION = 'Balance correction';
/**
 * The statement rule for a funding reversal pair (R-5a). `at`: the entry's time; `pair`: the other entry of its pair
 * (`isReversal`: that other entry is the reversal, so this one is the original), or null for an entry outside any pair. Months are calendar
 * months in UTC, as the CRM's statement periods are. Same month: both hidden. Original in an earlier month: that
 * month's statement stands (the original shown), and the reversal is a neutral balance correction in its own.
 */
export function statementLine(at, pair) {
    if (!pair)
        return 'shown';
    const month = (t) => new Date(t).toISOString().slice(0, 7);
    if (month(at) === month(pair.at))
        return 'hidden';
    return pair.isReversal ? 'shown' : 'balance_correction';
}
/** Filter groups for the wallet history (the client's own: never a reversal type); each maps to ledger types (sent to `/api/ledger?types=`). */
export const LEDGER_FILTERS = {
    all: null,
    money: ['deposit', 'withdrawal', 'withdrawal_refund', 'credit', 'cashback', 'referral', 'volume_reward', 'pro_transfer_out', 'pro_transfer_in', 'pro_transfer_refund',
        'dividend', 'capital_return', 'chargeback', 'nominal'],
    trade: ['realized_pnl'],
    fees: ['fee', 'swap', 'management_fee', 'performance_fee'],
    yield: ['yield_stake', 'yield_redeem', 'yield_interest'],
};
//# sourceMappingURL=ledger.js.map